import React, { useState } from "react";
import { EXPLORATION_LOCATIONS, getLocationByRoll } from "./helpers";
import { ExplorationResult, ExplorationLocation } from "./types";
import { LOCATION_EFFECTS, processLocationReward } from "./locationEffects";
import { useForceSaveOnUnmount } from "./hooks/useAutoSave";

interface ExplorationModalProps {
  onConfirm: (result: ExplorationResult) => void;
  onClose: () => void;
  locations: ExplorationLocation[];
}

export const ExplorationModal = ({ onConfirm, onClose, locations }: ExplorationModalProps) => {
  // Force save when modal closes
  useForceSaveOnUnmount();

  const [selectedTable, setSelectedTable] = useState("");
  const [inputMethod, setInputMethod] = useState(""); // "location" or "number"
  const [selectedLocation, setSelectedLocation] = useState("");
  const [manualNumber, setManualNumber] = useState("");

  const handleTableSelect = (table: "common" | "rare" | "legendary") => {
    setSelectedTable(table);
    setInputMethod("");
    setSelectedLocation("");
    setManualNumber("");
  };

  const handleConfirm = () => {
    if (!selectedTable) {
      alert("Please select a table");
      return;
    }

    if (!inputMethod) {
      alert("Please choose a location or enter a number");
      return;
    }

    if (inputMethod === "location") {
      if (!selectedLocation) {
        alert("Please select a location");
        return;
      }
      const roll = parseInt(selectedLocation);
      const location = getLocationByRoll(selectedTable, roll);
      const ducatsGained = roll * 10;

      onConfirm({
        type: "location",
        table: selectedTable as "common" | "rare" | "legendary",
        roll,
        location,
        ducatsGained,
      });
    } else if (inputMethod === "number") {
      const roll = parseInt(manualNumber);
      if (isNaN(roll) || roll <= 0) {
        alert("Please enter a valid number");
        return;
      }
      const ducatsGained = roll * 10;

      onConfirm({
        type: "number",
        table: selectedTable as "common" | "rare" | "legendary",
        roll,
        ducatsGained,
      });
    }
  };

  const getAvailableLocations = () => {
    if (!selectedTable) return [];
    return Object.entries(EXPLORATION_LOCATIONS[selectedTable as keyof typeof EXPLORATION_LOCATIONS]).map(
      ([roll, data]) => ({
        roll: parseInt(roll),
        ...data,
      })
    );
  };

  const isLocationAlreadyFound = (roll: string) => {
    return locations.some(
      (loc) => loc.table === selectedTable && loc.roll === parseInt(roll)
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-3">
      <div className="ledger-shell p-4 max-w-3xl w-full max-h-[90vh] overflow-y-auto text-sm">
        <h2 className="text-base font-semibold mb-3 uppercase tracking-wider">Exploration</h2>

        {/* Step 1: Choose Table */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-[#E6E5D3] mb-2 uppercase tracking-wider">
            Choose Exploration Table:
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => handleTableSelect("common")}
              className={`px-3 py-2 font-semibold text-xs uppercase tracking-wider ${
                selectedTable === "common"
                  ? "bg-[#E6E5D3] text-[#111]"
                  : "bg-[#222] text-[#E6E5D3] hover:bg-[#2a2a2a]"
              }`}
            >
              Common
            </button>
            <button
              onClick={() => handleTableSelect("rare")}
              className={`px-3 py-2 font-semibold text-xs uppercase tracking-wider ${
                selectedTable === "rare"
                  ? "bg-[#E6E5D3] text-[#111]"
                  : "bg-[#222] text-[#E6E5D3] hover:bg-[#2a2a2a]"
              }`}
            >
              Rare
            </button>
            <button
              onClick={() => handleTableSelect("legendary")}
              className={`px-3 py-2 font-semibold text-xs uppercase tracking-wider ${
                selectedTable === "legendary"
                  ? "bg-[#E6E5D3] text-[#111]"
                  : "bg-[#222] text-[#E6E5D3] hover:bg-[#2a2a2a]"
              }`}
            >
              Legendary
            </button>
          </div>
        </div>

        {/* Step 2: Choose Method */}
        {selectedTable && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-300 mb-3">
              Choose Input Method:
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setInputMethod("location")}
                className={`px-4 py-3 rounded font-semibold ${
                  inputMethod === "location"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                Select Location
              </button>
              <button
                onClick={() => setInputMethod("number")}
                className={`px-4 py-3 rounded font-semibold ${
                  inputMethod === "number"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                Enter Roll Number
              </button>
            </div>
          </div>
        )}

        {/* Step 3a: Select Location */}
        {selectedTable && inputMethod === "location" && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-300 mb-3">
              Select a Location:
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {getAvailableLocations().map((loc) => (
                <button
                  key={loc.roll}
                  onClick={() => setSelectedLocation(loc.roll.toString())}
                  className={`w-full text-left px-4 py-3 rounded ${
                    selectedLocation === loc.roll.toString()
                      ? "bg-blue-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold">
                        {loc.roll} – {loc.name}
                        {isLocationAlreadyFound(loc.roll.toString()) && (
                          <span className="ml-2 text-xs bg-yellow-600 px-2 py-1 rounded">
                            FOUND
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400 mt-1">
                        {LOCATION_EFFECTS[loc.name]?.effect || loc.effect}
                      </div>
                    </div>
                    <div className="text-yellow-400 font-semibold ml-4">
                      +{loc.roll * 10}₡
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3b: Enter Number */}
        {selectedTable && inputMethod === "number" && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-300 mb-3">
              Enter Your Roll:
            </h3>
            <input
              type="number"
              value={manualNumber}
              onChange={(e) => setManualNumber(e.target.value)}
              placeholder="Enter roll number..."
              className="w-full bg-gray-700 text-white px-4 py-3 rounded text-lg"
              min="1"
            />
            {manualNumber && (
              <div className="mt-2 text-yellow-400 font-semibold">
                You will gain: +{parseInt(manualNumber) * 10}₡
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleConfirm}
            disabled={
              !selectedTable ||
              !inputMethod ||
              (inputMethod === "location" && !selectedLocation) ||
              (inputMethod === "number" && !manualNumber)
            }
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Exploration
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
